import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { OccurrenceDto } from '@familynest/shared'
import { WEEKDAY_LABELS, formatTime, isSameDay, startOfDay, weekDays } from '../../lib/date'
import { groupByDaySpanning, isMultiDay, localDayKey } from './utils'

/**
 * Zeitraster für Woche/Tag (ADR-088): Uhrzeit-Spalte, Termine als absolut
 * positionierte Blöcke, Ganztags-/Mehrtagestermine oben als Leiste.
 *
 * Drag & Drop ohne Bibliothek (Pointer Events):
 * - Maus: Ziehen ab 4 px verschiebt, Ziehen am unteren Rand ändert die Dauer.
 * - Touch: Long-Press (400 ms) startet den Drag; vorher scrollt die Seite ganz
 *   normal. Während des Drags blockiert ein nicht-passiver touchmove-Listener
 *   das Scrollen (sonst bricht der Browser die Geste mit pointercancel ab).
 * - Alles rastet auf 15 Minuten ein. Gespeichert wird erst beim Loslassen
 *   (onMove); optimistisches Update + Rollback macht der Aufrufer.
 */

export const HOUR_PX = 48
const PX_PER_MIN = HOUR_PX / 60
const SNAP_MIN = 15
const MAX_COLS = 3 // nur Startwert, bis die Spaltenbreite gemessen ist
/**
 * Mindestbreite je Termin-Spalte, bei der Titel und Uhrzeit (11 px) noch lesbar
 * sind (~10 Zeichen). Daraus ergibt sich die Spaltenzahl (Tag und Woche) — erst
 * wenn selbst dann nicht alle parallelen Termine passen, kommt „+N".
 */
const MIN_COL_PX = 80
const LONG_PRESS_MS = 400
const MOUSE_THRESHOLD_PX = 4
const TOUCH_SLOP_PX = 8
const MIN_BLOCK_MIN = 20 // Mindesthöhe, damit kurze Termine anklickbar bleiben

/** Auf 15 Minuten runden. */
export function snapMinutes(min: number, step = SNAP_MIN): number {
  return Math.round(min / step) * step
}

const minutesOfDay = (d: Date): number => d.getHours() * 60 + d.getMinutes()

export type PlacedBlock = { occ: OccurrenceDto; startMin: number; endMin: number; col: number; cols: number }
export type Overflow = { startMin: number; count: number }

/**
 * Überlappungs-Layout eines Tages: transitive Überlappungsgruppen (Cluster),
 * darin Spalten per Greedy. Maximal `maxCols` Spalten — der Rest wird als „+N" gezählt.
 */
export function layoutDayBlocks(list: OccurrenceDto[], dayStart: Date, maxCols = MAX_COLS): { blocks: PlacedBlock[]; overflow: Overflow[] } {
  const items = list
    .map((occ) => {
      const s = new Date(occ.occurrenceStart)
      const e = new Date(occ.occurrenceEnd)
      const startMin = s.getTime() < dayStart.getTime() ? 0 : minutesOfDay(s)
      let endMin = isSameDay(e, dayStart) ? minutesOfDay(e) : 24 * 60
      if (endMin - startMin < MIN_BLOCK_MIN) endMin = Math.min(24 * 60, startMin + MIN_BLOCK_MIN)
      return { occ, startMin, endMin }
    })
    .sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin)

  const blocks: PlacedBlock[] = []
  const overflow: Overflow[] = []
  let cluster: Array<(typeof items)[number] & { col: number }> = []
  let clusterEnd = -1
  let colEnds: number[] = []

  const flush = (): void => {
    if (cluster.length === 0) return
    const cols = Math.min(colEnds.length, maxCols)
    let hidden = 0
    for (const c of cluster) {
      if (c.col < maxCols) blocks.push({ occ: c.occ, startMin: c.startMin, endMin: c.endMin, col: c.col, cols })
      else hidden++
    }
    if (hidden > 0) overflow.push({ startMin: cluster[0]!.startMin, count: hidden })
    cluster = []
    colEnds = []
    clusterEnd = -1
  }

  for (const it of items) {
    if (it.startMin >= clusterEnd) flush()
    let col = colEnds.findIndex((end) => end <= it.startMin)
    if (col === -1) {
      col = colEnds.length
      colEnds.push(it.endMin)
    } else {
      colEnds[col] = it.endMin
    }
    cluster.push({ ...it, col })
    clusterEnd = Math.max(clusterEnd, it.endMin)
  }
  flush()
  return { blocks, overflow }
}

type DragMode = 'move' | 'resize'
type DragState = {
  occ: OccurrenceDto
  mode: DragMode
  pointerType: string
  startX: number
  startY: number
  origDayIdx: number
  origStartMin: number
  origDurMin: number
  active: boolean
  dayIdx: number
  deltaMin: number
}

export function TimeGridView({
  mode,
  cursor,
  occurrences,
  colorFor,
  canDrag,
  canCreate,
  onEventClick,
  onSlotClick,
  onDayClick,
  onOverflow,
  onMove,
}: {
  mode: 'week' | 'day'
  cursor: Date
  occurrences: OccurrenceDto[]
  colorFor: (occ: OccurrenceDto) => string
  canDrag: (occ: OccurrenceDto) => boolean
  canCreate: boolean
  onEventClick: (occ: OccurrenceDto) => void
  onSlotClick: (at: Date) => void
  onDayClick: (day: Date) => void
  onOverflow: (day: Date) => void
  onMove: (occ: OccurrenceDto, start: Date, end: Date) => void
}): React.JSX.Element {
  const days = useMemo(() => (mode === 'week' ? weekDays(cursor) : [startOfDay(cursor)]), [mode, cursor])
  const scrollRef = useRef<HTMLDivElement>(null)
  const colRefs = useRef<Array<HTMLDivElement | null>>([])
  const dragRef = useRef<DragState | null>(null)
  const timerRef = useRef<number | null>(null)
  const suppressClickRef = useRef(false)
  const [drag, setDrag] = useState<DragState | null>(null)
  const [now, setNow] = useState(() => new Date())
  const [colWidth, setColWidth] = useState(0)

  // Breite einer Tagesspalte messen (Fenster/iPad-Drehung) → Spaltenzahl. Alle
  // Tagesspalten sind gleich breit; `days` als Abhängigkeit, weil die Spalten
  // beim Blättern neu entstehen (key = Datum) und der Observer sonst am alten hängt.
  useLayoutEffect(() => {
    const el = colRefs.current[0]
    if (!el) return
    setColWidth(el.clientWidth)
    const ro = new ResizeObserver(() => setColWidth(el.clientWidth))
    ro.observe(el)
    return () => ro.disconnect()
  }, [days])
  const maxCols = colWidth > 0 ? Math.max(1, Math.floor(colWidth / MIN_COL_PX)) : MAX_COLS

  // Beim Öffnen auf 06:00 scrollen (sichtbar 06–22, scrollbar 00–24).
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 6 * HOUR_PX - 8
  }, [])
  // Jetzt-Linie minütlich nachziehen.
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60_000)
    return () => window.clearInterval(t)
  }, [])

  // Oben: ganztägig, mehrtägig, Hintergrund-Abos (Feiertage/Ferien). Raster: der Rest.
  const isTop = (o: OccurrenceDto): boolean => o.allDay || isMultiDay(o) || !!o.renderAsBackground
  const topByDay = useMemo(() => groupByDaySpanning(occurrences.filter(isTop), days), [occurrences, days])
  const timedByDay = useMemo(() => {
    const m = new Map<string, OccurrenceDto[]>()
    for (const d of days) m.set(localDayKey(d), [])
    for (const o of occurrences) {
      if (isTop(o)) continue
      m.get(localDayKey(new Date(o.occurrenceStart)))?.push(o)
    }
    return m
  }, [occurrences, days])

  // ── Drag-Logik ───────────────────────────────────────────────────
  // Window-Listener brauchen STABILE Funktionsreferenzen (sonst entfernt
  // removeEventListener aus einem späteren Render nie den alten Listener). Die
  // stabilen Wrapper rufen die jeweils aktuelle Render-Fassung über ein Ref auf.
  const live = useRef({ move: (_e: PointerEvent) => {}, up: () => {}, cancel: () => {}, touch: (_e: TouchEvent) => {} })
  const stable = useMemo(
    () => ({
      move: (e: PointerEvent): void => live.current.move(e),
      up: (): void => live.current.up(),
      cancel: (): void => live.current.cancel(),
      touch: (e: TouchEvent): void => live.current.touch(e),
    }),
    [],
  )

  const cleanup = (): void => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    timerRef.current = null
    window.removeEventListener('pointermove', stable.move)
    window.removeEventListener('pointerup', stable.up)
    window.removeEventListener('pointercancel', stable.cancel)
    document.removeEventListener('touchmove', stable.touch)
    dragRef.current = null
    setDrag(null)
  }

  function blockTouchScroll(e: TouchEvent): void {
    if (dragRef.current?.active) e.preventDefault()
  }

  function activate(): void {
    const d = dragRef.current
    if (!d) return
    d.active = true
    setDrag({ ...d })
    if (d.pointerType !== 'mouse') navigator.vibrate?.(10)
  }

  function dayIndexAt(clientX: number): number {
    if (mode === 'day') return 0
    for (let i = 0; i < colRefs.current.length; i++) {
      const r = colRefs.current[i]?.getBoundingClientRect()
      if (r && clientX >= r.left && clientX < r.right) return i
    }
    const first = colRefs.current[0]?.getBoundingClientRect()
    return first && clientX < first.left ? 0 : days.length - 1
  }

  function onPointerMove(e: PointerEvent): void {
    const d = dragRef.current
    if (!d) return
    const dx = e.clientX - d.startX
    const dy = e.clientY - d.startY
    if (!d.active) {
      const dist = Math.hypot(dx, dy)
      if (d.pointerType === 'mouse') {
        if (dist >= MOUSE_THRESHOLD_PX) activate()
        else return
      } else {
        // Touch bewegt sich vor dem Long-Press → das ist Scrollen, kein Drag.
        if (dist > TOUCH_SLOP_PX) cleanup()
        return
      }
    }
    d.deltaMin = snapMinutes(dy / PX_PER_MIN)
    if (d.mode === 'move') d.dayIdx = dayIndexAt(e.clientX)
    setDrag({ ...d })
  }

  function preview(d: DragState): { dayIdx: number; startMin: number; endMin: number } {
    if (d.mode === 'resize') {
      const dur = Math.max(SNAP_MIN, snapMinutes(d.origDurMin + d.deltaMin))
      return { dayIdx: d.origDayIdx, startMin: d.origStartMin, endMin: Math.min(24 * 60, d.origStartMin + dur) }
    }
    const start = Math.max(0, Math.min(24 * 60 - SNAP_MIN, snapMinutes(d.origStartMin + d.deltaMin)))
    return { dayIdx: d.dayIdx, startMin: start, endMin: start + d.origDurMin }
  }

  function onPointerUp(): void {
    const d = dragRef.current
    if (d?.active) {
      suppressClickRef.current = true
      window.setTimeout(() => (suppressClickRef.current = false), 0)
      const p = preview(d)
      const changed = p.dayIdx !== d.origDayIdx || p.startMin !== d.origStartMin || p.endMin - p.startMin !== d.origDurMin
      if (changed) {
        const day = days[p.dayIdx]!
        const start = new Date(day)
        start.setHours(0, p.startMin, 0, 0)
        const end = new Date(start.getTime() + (p.endMin - p.startMin) * 60_000)
        onMove(d.occ, start, end)
      }
    }
    cleanup()
  }

  function onPointerCancel(): void {
    cleanup()
  }

  live.current = { move: onPointerMove, up: onPointerUp, cancel: onPointerCancel, touch: blockTouchScroll }

  function startDrag(e: React.PointerEvent, occ: OccurrenceDto, dmode: DragMode, dayIdx: number, startMin: number): void {
    if (!canDrag(occ) || (e.pointerType === 'mouse' && e.button !== 0)) return
    if (dmode === 'resize') e.stopPropagation()
    const dur = Math.round((Date.parse(occ.occurrenceEnd) - Date.parse(occ.occurrenceStart)) / 60_000)
    dragRef.current = {
      occ,
      mode: dmode,
      pointerType: e.pointerType,
      startX: e.clientX,
      startY: e.clientY,
      origDayIdx: dayIdx,
      origStartMin: startMin,
      origDurMin: dur,
      active: false,
      dayIdx,
      deltaMin: 0,
    }
    window.addEventListener('pointermove', stable.move)
    window.addEventListener('pointerup', stable.up)
    window.addEventListener('pointercancel', stable.cancel)
    if (e.pointerType !== 'mouse') {
      document.addEventListener('touchmove', stable.touch, { passive: false })
      timerRef.current = window.setTimeout(activate, LONG_PRESS_MS)
    }
  }

  // Beim Unmount laufende Listener abräumen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => cleanup(), [])

  function slotClick(e: React.MouseEvent<HTMLDivElement>, day: Date): void {
    if (!canCreate || suppressClickRef.current || e.target !== e.currentTarget) return
    const rect = e.currentTarget.getBoundingClientRect()
    const min = Math.max(0, Math.min(24 * 60 - 30, Math.floor((e.clientY - rect.top) / PX_PER_MIN / 30) * 30))
    const at = new Date(day)
    at.setHours(0, min, 0, 0)
    onSlotClick(at)
  }

  const gridCols = { gridTemplateColumns: `3rem repeat(${days.length}, minmax(0, 1fr))` }
  const today = new Date()
  const p = drag?.active ? preview(drag) : null

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[var(--radius-card)] border" style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-card)' }}>
      {/* Kopf: Wochentage */}
      <div className="grid border-b" style={{ ...gridCols, borderColor: 'var(--color-line)' }}>
        <div />
        {days.map((day, i) => {
          const isToday = isSameDay(day, today)
          return (
            <button key={day.toISOString()} onClick={() => onDayClick(day)} className="px-1 py-1.5 text-center text-xs hover:brightness-95" style={{ color: isToday ? 'var(--color-accent)' : 'var(--color-muted)' }}>
              <span className="block">{WEEKDAY_LABELS[(day.getDay() + 6) % 7]}</span>
              <span className="text-base font-semibold" style={{ color: isToday ? 'var(--color-accent)' : 'var(--color-ink)' }} data-col={i}>
                {day.getDate()}
              </span>
            </button>
          )
        })}
      </div>

      {/* Ganztags-/Mehrtages-Leiste */}
      <div className="grid border-b" style={{ ...gridCols, borderColor: 'var(--color-line)' }}>
        <div className="px-1 py-1 text-right text-[10px]" style={{ color: 'var(--color-muted)' }}>ganzt.</div>
        {days.map((day) => {
          const list = topByDay.get(localDayKey(day)) ?? []
          return (
            <div key={day.toISOString()} className="flex min-h-7 flex-col gap-0.5 border-l p-0.5" style={{ borderColor: 'var(--color-line)' }}>
              {list.map((o) => {
                const color = colorFor(o)
                const bg = !!o.renderAsBackground
                return (
                  <button
                    key={o.id + o.occurrenceStart}
                    onClick={() => onEventClick(o)}
                    title={o.title}
                    className="truncate rounded px-1 text-left text-[11px] leading-4"
                    style={
                      bg
                        ? { color: 'var(--color-muted)', backgroundColor: `color-mix(in srgb, ${color} 10%, var(--color-card))` }
                        : { color: 'var(--color-ink)', backgroundColor: `color-mix(in srgb, ${color} var(--event-bar-tint, 13%), var(--color-card))`, borderLeft: `3px solid ${color}` }
                    }
                  >
                    {o.title}
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>

      {/* Raster (scrollbar 00–24) */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="relative grid" style={{ ...gridCols, height: 24 * HOUR_PX }}>
          {/* Uhrzeiten */}
          <div className="relative">
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="absolute right-1 -translate-y-1/2 text-[10px]" style={{ top: h * HOUR_PX, color: 'var(--color-muted)' }}>
                {h === 0 ? '' : `${String(h).padStart(2, '0')}:00`}
              </div>
            ))}
          </div>

          {days.map((day, dayIdx) => {
            const { blocks, overflow } = layoutDayBlocks(timedByDay.get(localDayKey(day)) ?? [], startOfDay(day), maxCols)
            const isToday = isSameDay(day, now)
            return (
              <div
                key={day.toISOString()}
                ref={(el) => {
                  colRefs.current[dayIdx] = el
                }}
                className="relative border-l"
                style={{
                  borderColor: 'var(--color-line)',
                  backgroundImage: `repeating-linear-gradient(to bottom, var(--color-line) 0, var(--color-line) 1px, transparent 1px, transparent ${HOUR_PX}px)`,
                  cursor: canCreate ? 'cell' : 'default',
                }}
                onClick={(e) => slotClick(e, day)}
              >
                {blocks.map((b) => {
                  const color = colorFor(b.occ)
                  const isDragged = drag?.active && drag.occ === b.occ
                  const draggable = canDrag(b.occ)
                  const widthPct = 100 / b.cols
                  return (
                    <div
                      key={b.occ.id + b.occ.occurrenceStart}
                      role="button"
                      tabIndex={0}
                      onPointerDown={(e) => startDrag(e, b.occ, 'move', dayIdx, b.startMin)}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!suppressClickRef.current) onEventClick(b.occ)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') onEventClick(b.occ)
                      }}
                      onContextMenu={(e) => draggable && e.preventDefault()}
                      title={`${formatTime(b.occ.occurrenceStart)} ${b.occ.title}`}
                      className="absolute overflow-hidden rounded-md px-1 py-0.5 text-[11px] leading-tight"
                      style={{
                        top: b.startMin * PX_PER_MIN + 1,
                        height: Math.max(MIN_BLOCK_MIN, b.endMin - b.startMin) * PX_PER_MIN - 2,
                        left: `calc(${b.col * widthPct}% + 2px)`,
                        width: `calc(${widthPct}% - 4px)`,
                        backgroundColor: `color-mix(in srgb, ${color} 22%, var(--color-card))`,
                        borderLeft: `3px solid ${color}`,
                        color: 'var(--color-ink)',
                        opacity: isDragged ? 0.35 : 1,
                        cursor: draggable ? 'grab' : 'pointer',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        WebkitTouchCallout: 'none',
                      }}
                    >
                      <div className="truncate font-medium">{b.occ.title}</div>
                      <div className="truncate" style={{ color: 'var(--color-muted)' }}>
                        {formatTime(b.occ.occurrenceStart)}
                        {b.occ.location ? ` · ${b.occ.location}` : ''}
                      </div>
                      {draggable && (
                        <div
                          className="absolute inset-x-0 bottom-0 h-2"
                          style={{ cursor: 'ns-resize' }}
                          onPointerDown={(e) => startDrag(e, b.occ, 'resize', dayIdx, b.startMin)}
                          aria-hidden
                        />
                      )}
                    </div>
                  )
                })}

                {overflow.map((ov) => (
                  <button
                    key={ov.startMin}
                    onClick={(e) => {
                      e.stopPropagation()
                      onOverflow(day)
                    }}
                    className="absolute right-0.5 rounded-full px-1.5 text-[10px] font-semibold"
                    style={{ top: ov.startMin * PX_PER_MIN + 2, backgroundColor: 'var(--color-accent)', color: '#fff' }}
                    title={`${ov.count} weitere Termine`}
                  >
                    +{ov.count}
                  </button>
                ))}

                {/* Vorschau beim Ziehen */}
                {p && drag && p.dayIdx === dayIdx && (
                  <div
                    className="pointer-events-none absolute inset-x-0.5 rounded-md border-2 border-dashed px-1 text-[11px]"
                    style={{
                      top: p.startMin * PX_PER_MIN,
                      height: (p.endMin - p.startMin) * PX_PER_MIN,
                      borderColor: colorFor(drag.occ),
                      backgroundColor: `color-mix(in srgb, ${colorFor(drag.occ)} 30%, transparent)`,
                      color: 'var(--color-ink)',
                    }}
                  >
                    {fmtMin(p.startMin)}–{fmtMin(p.endMin)}
                  </div>
                )}

                {isToday && (
                  <div className="pointer-events-none absolute inset-x-0 h-0.5" style={{ top: minutesOfDay(now) * PX_PER_MIN, backgroundColor: 'var(--color-danger)' }} />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function fmtMin(m: number): string {
  return `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}
